import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Eye, Maximize2 } from "lucide-react";
import { FlyerPreviewLarge } from "@/components/marketing/FlyerPreviewLarge";

export const FlyerPreview = ({ assetId, form, strip = true }) => {
  const [page, setPage] = useState(0);
  const [img, setImg] = useState(null);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [big, setBig] = useState(false);
  const key = JSON.stringify(form);

  useEffect(() => {
    if (!form?.shop_name?.trim()) return undefined;
    let url = null;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true); setErr("");
      try {
        const res = await api.post(`/marketing/assets/${assetId}/personalized/preview`, form, { params: { page }, responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setImg((old) => { if (old) URL.revokeObjectURL(old); return url; });
        setPages(Number(res.headers["x-page-count"]) || 1);
      } catch { if (!cancelled) setErr("Preview unavailable right now — you can still download."); }
      finally { if (!cancelled) setLoading(false); }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [assetId, key, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (img) URL.revokeObjectURL(img); }, [img]);

  return (
    <div className="space-y-2" data-testid="flyer-preview">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5 font-mono uppercase tracking-widest"><Eye className="w-3.5 h-3.5" />Preview</span>
        <span data-testid="flyer-preview-pages">{pages > 1 ? `Page ${page + 1} of ${pages}${strip ? " · strip on every page" : ""}` : "Page 1"}</span>
      </div>
      {pages > 1 && (
        <div className="flex gap-1" data-testid="flyer-preview-page-tabs">
          {Array.from({ length: pages }, (_, i) => (
            <button key={i} type="button" onClick={() => setPage(i)} data-testid={`flyer-preview-page-${i + 1}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${page === i ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {pages === 2 && !strip ? (i === 0 ? "Front" : "Back") : `Page ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={() => img && setBig(true)} disabled={!img} className="group relative w-full rounded-xl border border-border bg-white overflow-hidden min-h-[260px] flex items-center justify-center cursor-zoom-in disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Enlarge preview" data-testid="flyer-preview-enlarge">
        {img && <img src={img} alt="Personalized flyer preview" className={`w-full h-auto transition-opacity duration-300 ${loading ? "opacity-50" : "opacity-100"}`} data-testid="flyer-preview-image" />}
        {img && <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-slate-900/80 text-white text-[11px] font-medium px-2.5 py-1 transition-transform duration-200 group-hover:scale-105"><Maximize2 className="w-3 h-3" />Click to enlarge</span>}
        {loading && <Loader2 className="absolute w-6 h-6 animate-spin text-primary" data-testid="flyer-preview-loading" />}
        {!img && !loading && <div className="text-xs text-slate-500 p-6 text-center" data-testid="flyer-preview-empty">{err || "Enter a shop name to see the preview."}</div>}
      </button>
      {img && err && <div className="text-xs text-destructive">{err}</div>}
      <FlyerPreviewLarge open={big} onOpenChange={setBig} assetId={assetId} form={form} page={page} strip={strip} />
    </div>
  );
};
